import * as React from 'react';
import {createContext, useEffect, useReducer} from 'react';
import axios from 'axios';
import {userApi} from '../utils/apiClient.tsx';

// third-party
import {Auth0Client} from '@auth0/auth0-spa-js';

// reducer - state management
import {LOGIN, LOGOUT} from '../store/actions.ts';
import accountReducer from '../store/accountReducer.ts';

// types
import {Auth0ContextType, InitialLoginContextProps, KeyedObject} from '../../interfaces/auth';

// constant
let auth0Client: Auth0Client;

const LOCAL_AUTH_STORAGE_KEY = 'local_auth_user';
const isLocalAuthMode = process.env.REACT_APP_AUTH_MODE === 'local';

const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

// ==============================|| AUTH0 CONTEXT & PROVIDER ||============================== //

const Auth0Context = createContext<Auth0ContextType | null>(null);

export const Auth0Provider = ({ children }: { children: React.ReactElement }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    useEffect(() => {
        const init = async () => {
            if (process.env.REACT_APP_BYPASS_AUTH === 'true') {
                dispatch({
                    type: LOGIN,
                    payload: {
                        isLoggedIn: true,
                        user: { id: 'kdshbvjvbshdf', name: 'dev', nickname: 'dev', email: 'lviscardi@rtl.it' }
                    }
                });
                return;
            }
            if (isLocalAuthMode) {
                const storedUser = localStorage.getItem(LOCAL_AUTH_STORAGE_KEY);
                if (storedUser) {
                    dispatch({ type: LOGIN, payload: { isLoggedIn: true, user: JSON.parse(storedUser) } });
                } else {
                    dispatch({ type: LOGOUT });
                }
                return;
            }
            try {
                auth0Client = new Auth0Client({
                    clientId: process.env.REACT_APP_AUTH0_CLIENT_ID as string,
                    domain: process.env.REACT_APP_AUTH0_DOMAIN as string,
                    authorizationParams: {
                        redirect_uri: window.location.origin,
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE as string,
                    }
                });
                await auth0Client.checkSession();
                const isLoggedIn = await auth0Client.isAuthenticated();
                if (isLoggedIn) {
                    const user = await auth0Client.getUser();
                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            user: {
                                id: user?.sub,
                                name: user?.email,
                                nickname: user?.nickanme,
                                email: user?.email
                            }
                        }
                    });
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                throw new Error('Errore di certificato');
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    const login = async (username?: string, password?: string) => {
            if (isLocalAuthMode) {
                try {
                    const response = await axios.post(process.env.REACT_APP_BACKEND_URL + 'local_auth/login', {
                        username,
                        password
                    });
                    if (!response.data?.username) {
                        throw new Error('Credenziali non valide');
                    }
                    const user = { id: response.data.username, name: response.data.nome_cognome, email: response.data.username };
                    localStorage.setItem(LOCAL_AUTH_STORAGE_KEY, JSON.stringify(user));
                    dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
                } catch (error) {
                    throw new Error('Credenziali non valide');
                }
                return;
            }

            const isLoggedIn = await auth0Client.isAuthenticated();
            if (isLoggedIn) {
                const user = await auth0Client.getUser();
                dispatch({
                    type: LOGIN,
                    payload: {
                        isLoggedIn: true,
                        user: {
                            email: user?.email,
                            name: user?.name,
                        }
                    }
                });
            }
    };

    const logout = () => {
        if (isLocalAuthMode) {
            localStorage.removeItem(LOCAL_AUTH_STORAGE_KEY);
            dispatch({ type: LOGOUT });
            return;
        }

        auth0Client.logout({
            clientId: process.env.REACT_APP_AUTH0_CLIENT_ID as string,
            //returnTo: window.location.origin + '/login'
        });

        dispatch({
            type: LOGOUT
        });
    };

    const getToken = async () => {
        if (process.env.REACT_APP_BYPASS_AUTH === 'true') return 'dev-token';
        if (isLocalAuthMode) {
            const storedUser = localStorage.getItem(LOCAL_AUTH_STORAGE_KEY);
            return storedUser ? JSON.parse(storedUser).id : '';
        }
        try {
            return await auth0Client.getTokenSilently();
        } catch (error) {
            console.error('Error getting token:', error);
            throw error;
        }
    };

    const resetPassword = async (oldPassword: string, newPassword: string) => {
        if (!isLocalAuthMode) {
            throw new Error('Operazione non disponibile per questa modalità di autenticazione');
        }
        const storedUser = localStorage.getItem(LOCAL_AUTH_STORAGE_KEY);
        const username = storedUser ? JSON.parse(storedUser).id : null;
        if (!username) {
            throw new Error('Utente non autenticato');
        }
        await userApi.post('local_auth/change_password', {
            username,
            old_password: oldPassword,
            new_password: newPassword
        });
    };

    const updateProfile = () => {};


    return (
        <Auth0Context.Provider value={{ ...state, isLocalAuthMode, login, logout, resetPassword, updateProfile, getToken }}>
            {children}
        </Auth0Context.Provider>
    );
};

export default Auth0Context;